import { useTranslation } from 'react-i18next';
import { DatePicker, Tooltip } from 'antd';
import clsx from 'clsx';
import dayjs from 'dayjs';
import 'dayjs/locale/en';
import 'dayjs/locale/ru';
import 'dayjs/locale/sr';
import 'dayjs/locale/sq';
import 'dayjs/locale/pl';
import localeData from 'dayjs/plugin/localeData';
import updateLocale from 'dayjs/plugin/updateLocale';
import weekday from 'dayjs/plugin/weekday';
import i18n from '@/i18n';
import { DATE_FORMATS, isDisabledWeekendAndHoliday } from '@/utils/formUtils';

dayjs.extend(localeData);
dayjs.extend(updateLocale);
dayjs.extend(weekday);
dayjs.updateLocale('en', { weekStart: 1 });

export default function CustomRangePicker({
  form,
  name = 'dateOfLeave',
  markedDates = [],
  onMonthChange,
  disabled = false,
  allowWeekendLeaves = false,
  publicHolidays = [],
}) {
  const { t } = useTranslation(['menus', 'forms', 'messages']);
  const lang = i18n.language;
  const value = form.getFieldValue(name);

  const getDateMeta = (date) => {
    const formatted = dayjs(date).format('YYYY-MM-DD');
    return markedDates.find((d) => d.selectedDate === formatted);
  };

  return (
    <DatePicker.RangePicker
      allowEmpty
      disabled={disabled}
      value={value}
      onChange={(dates) => {
        if (disabled) return;
        form.setFieldsValue({ [name]: dates });
        form.onChange?.(dates);
      }}
      className="ant-range-picker w-full"
      format={DATE_FORMATS.DISPLAY}
      locale={{ lang: { locale: lang } }}
      placeholder={[
        t('startDate', { ns: 'forms' }),
        t('endDate', { ns: 'forms' }),
      ]}
      disabledDate={(current) => {
        const metadata = getDateMeta(current);
        const isMarkedAsDisabled = metadata?.disabled === true;
        const isHolidayOrWeekend = isDisabledWeekendAndHoliday(
          current,
          allowWeekendLeaves,
          publicHolidays
        );

        return isMarkedAsDisabled || isHolidayOrWeekend;
      }}
      onPanelChange={(dates) => {
        if (dates?.[0]) {
          const startMonth = dayjs(dates[0]).startOf('month');
          const endMonth = dayjs(dates[0]).add(1, 'month').endOf('month');

          onMonthChange(startMonth, endMonth);
        }
      }}
      cellRender={(current) => {
        const metadata = getDateMeta(current);
        const isMarked = !!metadata;
        const bgColor = metadata?.workType?.color?.argb
          ? `#${metadata.workType.color.argb}`
          : undefined;
        const textColor = metadata?.workType?.textColor || 'white';
        const label = metadata?.workType?.label;

        const cellContent = (
          <div
            className={clsx('ant-picker-cell-inner', {
              'rounded-md': isMarked,
            })}
            style={{
              backgroundColor: isMarked ? bgColor : undefined,
              color: isMarked ? textColor : undefined,
            }}
          >
            {current.date()}
          </div>
        );

        return label ? (
          <Tooltip title={label}>{cellContent}</Tooltip>
        ) : (
          cellContent
        );
      }}
    />
  );
}
